import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Loader from "../components/Loader";
import dealerService from "../services/dealerService";

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const UserAvatar = ({ url, name, role = "dealer" }) => {
  const [errorLevel, setErrorLevel] = useState(0);

  const seed = encodeURIComponent(name || role || "avatar");

  // Multi-tier professional avatar sources
  const avatarSources = [
    url,
    `https://i.pravatar.cc/150?u=${seed}`,
    `https://api.dicebear.com/7.x/personas/svg?seed=${seed}&backgroundColor=0f172a`,
    `https://ui-avatars.com/api/?name=${seed}&background=${role === "dealer" ? "d4af37" : role === "shop" ? "3b82f6" : "10b981"
    }&color=ffffff&bold=true`,
  ].filter(Boolean);

  const currentSrc =
    avatarSources[errorLevel] || avatarSources[avatarSources.length - 1];

  const getRoleIcon = () => {
    if (role === "dealer") return "bi-person-fill text-warning";
    if (role === "shop") return "bi-shop text-info";
    return "bi-person-fill text-success";
  };

  return (
    <div className={`hierarchy-avatar-container ${role}`}>
      {errorLevel < avatarSources.length ? (
        <img
          src={currentSrc}
          alt={name || "Avatar"}
          className="hierarchy-avatar-img"
          onError={() => setErrorLevel((prev) => prev + 1)}
        />
      ) : (
        <i className={`bi ${getRoleIcon()} hierarchy-avatar-icon`} />
      )}
    </div>
  );
};

const AdminHierarchy = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hierarchy, setHierarchy] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedDealers, setExpandedDealers] = useState({});
  const [expandedShops, setExpandedShops] = useState({});

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      const data = await dealerService.getHierarchy();
      if (!data?.success) {
        throw new Error(data?.message || "Unable to load hierarchy.");
      }
      setSummary(data.summary || {});
      setHierarchy(Array.isArray(data.hierarchy) ? data.hierarchy : []);
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || "Unable to load hierarchy."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHierarchy();
  }, []);

  const filteredDealers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return hierarchy;

    return hierarchy.filter((dealer) => {
      const dealerHit = [dealer.full_name, dealer.username, dealer.mobile]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));

      const shopHit = (dealer.shops || []).some((shop) =>
        [shop.shop_name, shop.username, shop.mobile]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
      );

      const playerHit = (dealer.shops || []).some((shop) =>
        (shop.players || []).some((player) =>
          [player.full_name, player.username, player.mobile]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(term))
        )
      );

      const computerHit = (dealer.shops || []).some((shop) =>
        (shop.computers || []).some((comp) =>
          [comp.computer_code, comp.status]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(term))
        )
      );

      return dealerHit || shopHit || playerHit || computerHit;
    });
  }, [hierarchy, search]);

  const toggleDealer = (dealerId) => {
    setExpandedDealers((current) => ({
      ...current,
      [dealerId]: !current[dealerId],
    }));
  };

  const toggleShop = (shopId) => {
    setExpandedShops((current) => ({
      ...current,
      [shopId]: !current[shopId],
    }));
  };

  const game = summary?.game_summary || {};

  return (
    <div className="d-flex flex-column min-vh-100 app-shell">
      <Navbar toggleSidebar={() => setSidebarOpen((value) => !value)} />
      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={() => setSidebarOpen(false)}
        />

        <main className="app-main flex-grow-1 overflow-auto">
          <div className="container-fluid admin-page-wrap">
            <div className="admin-page-header">
              <div>
                <span className="admin-page-kicker">NETWORK CONTROL</span>
                <h2>Dealer → Shop → Computer ID Hierarchy</h2>
                <p>One live view of ownership, computer terminals, balances, and game performance.</p>
              </div>
              <button
                className="btn btn-outline-primary"
                onClick={loadHierarchy}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-2" /> Refresh
              </button>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <span className="admin-stat-icon tone-primary">
                    <i className="bi bi-people-fill" />
                  </span>
                  <div>
                    <div className="admin-stat-label">Dealers</div>
                    <div className="admin-stat-value">
                      {summary?.total_dealers || 0}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <span className="admin-stat-icon tone-warning">
                    <i className="bi bi-shop" />
                  </span>
                  <div>
                    <div className="admin-stat-label">Shops</div>
                    <div className="admin-stat-value">
                      {summary?.total_shops || 0}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <span className="admin-stat-icon tone-info">
                    <i className="bi bi-pc-display" />
                  </span>
                  <div>
                    <div className="admin-stat-label">Computers</div>
                    <div className="admin-stat-value">
                      {summary?.total_computers || 0}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <span className="admin-stat-icon tone-success">
                    <i className="bi bi-dice-5-fill" />
                  </span>
                  <div>
                    <div className="admin-stat-label">Total Bets</div>
                    <div className="admin-stat-value">
                      {game.total_bets || 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="admin-panel mb-4">
              <div className="admin-panel-head flex-wrap gap-3">
                <div>
                  <h5>Live Network</h5>
                  <p>Expand a dealer, then a shop, to inspect connected Computer IDs.</p>
                </div>
                <div className="admin-search-wrap">
                  <i className="bi bi-search" />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search dealer, shop or computer..."
                  />
                </div>
              </div>

              {loading ? (
                <div className="p-5">
                  <Loader message="Loading hierarchy..." />
                </div>
              ) : filteredDealers.length === 0 ? (
                <div className="admin-empty-state">
                  <i className="bi bi-diagram-3" />
                  <strong>No matching network records</strong>
                  <span>Try another search term.</span>
                </div>
              ) : (
                <div className="hierarchy-list">
                  {filteredDealers.map((dealer) => {
                    const dealerOpen = Boolean(expandedDealers[dealer.id]);
                    const dealerGame = dealer.game_summary || {};
                    return (
                      <section className="hierarchy-dealer" key={dealer.id}>
                        <button
                          type="button"
                          className="hierarchy-dealer-head"
                          onClick={() => toggleDealer(dealer.id)}
                        >
                          <div className="hierarchy-identity">
                            <UserAvatar
                              url={dealer.avatar_url}
                              name={dealer.full_name || dealer.username}
                              role="dealer"
                            />
                            <div>
                              <strong>
                                {dealer.full_name || dealer.username}
                              </strong>
                              <span>
                                @{dealer.username} · #{dealer.id}
                              </span>
                            </div>
                          </div>
                          <div className="hierarchy-dealer-meta">
                            <span>
                              <i className="bi bi-shop" />{" "}
                              {dealer.total_shops || 0} Shops
                            </span>
                            <span>
                              <i className="bi bi-pc-display" />{" "}
                              {dealer.total_computers || 0} Computers
                            </span>
                            <span>
                              <i className="bi bi-coin" /> {money(dealer.wallet)}
                            </span>
                            <span
                              className={`status-pill ${dealer.status === "ACTIVE"
                                  ? "active"
                                  : "blocked"
                                }`}
                            >
                              {dealer.status}
                            </span>
                            <i
                              className={`bi bi-chevron-${dealerOpen ? "up" : "down"
                                }`}
                            />
                          </div>
                        </button>

                        {dealerOpen && (
                          <div className="hierarchy-dealer-body">
                            <div className="hierarchy-game-strip">
                              <span>
                                <small>Bets</small>
                                <strong>{dealerGame.total_bets || 0}</strong>
                              </span>
                              <span>
                                <small>Bet Amount</small>
                                <strong>
                                  🪙 {money(dealerGame.total_bet_amount)}
                                </strong>
                              </span>
                              <span>
                                <small>Wins / Losses</small>
                                <strong>
                                  {dealerGame.total_wins || 0} /{" "}
                                  {dealerGame.total_losses || 0}
                                </strong>
                              </span>
                              <span>
                                <small>Pending Claims</small>
                                <strong>
                                  {dealerGame.pending_claims || 0}
                                </strong>
                              </span>
                              <span>
                                <small>Pending Amount</small>
                                <strong>
                                  🪙 {money(dealerGame.pending_amount)}
                                </strong>
                              </span>
                            </div>

                            {(dealer.shops || []).length === 0 ? (
                              <div className="p-4 text-secondary">
                                No shops under this dealer.
                              </div>
                            ) : (
                              (dealer.shops || []).map((shop) => {
                                const shopOpen = Boolean(
                                  expandedShops[shop.shop_id]
                                );
                                const shopGame = shop.game_summary || {};
                                const shopComputersList = shop.computers || [];
                                const shopPlayersList = shop.players || [];
                                return (
                                  <div
                                    className="hierarchy-shop"
                                    key={shop.shop_id}
                                  >
                                    <button
                                      type="button"
                                      className="hierarchy-shop-head"
                                      onClick={() => toggleShop(shop.shop_id)}
                                    >
                                      <div className="hierarchy-identity">
                                        <UserAvatar
                                          url={shop.avatar_url}
                                          name={shop.shop_name || shop.username}
                                          role="shop"
                                        />
                                        <div>
                                          <strong>
                                            {shop.shop_name || shop.username}
                                          </strong>
                                          <span>
                                            @{shop.username} · Owner #
                                            {shop.owner_id} ·{" "}
                                            {shop.total_computers || shopComputersList.length || 0} Computers
                                          </span>
                                        </div>
                                      </div>
                                      <div className="hierarchy-shop-meta">
                                        <span>🪙 {money(shop.wallet)}</span>
                                        <span
                                          className={`status-pill ${shop.owner_status === "ACTIVE" &&
                                              shop.shop_status === "ACTIVE"
                                              ? "active"
                                              : "blocked"
                                            }`}
                                        >
                                          {shop.owner_status === "ACTIVE" &&
                                            shop.shop_status === "ACTIVE"
                                            ? "ACTIVE"
                                            : "BLOCKED"}
                                        </span>
                                        <i
                                          className={`bi bi-chevron-${shopOpen ? "up" : "down"
                                            }`}
                                        />
                                      </div>
                                    </button>

                                    {shopOpen && (
                                      <div className="hierarchy-shop-body">
                                        <div className="hierarchy-shop-summary">
                                          <span>
                                            Bets{" "}
                                            <strong>
                                              {shopGame.total_bets || 0}
                                            </strong>
                                          </span>
                                          <span>
                                            Wins{" "}
                                            <strong>
                                              {shopGame.total_wins || 0}
                                            </strong>
                                          </span>
                                          <span>
                                            Losses{" "}
                                            <strong>
                                              {shopGame.total_losses || 0}
                                            </strong>
                                          </span>
                                          <span>
                                            Pending{" "}
                                            <strong>
                                              {shopGame.pending_claims || 0}
                                            </strong>
                                          </span>
                                        </div>

                                        {shopComputersList.length > 0 ? (
                                          <div className="table-responsive">
                                            <table className="table hierarchy-player-table align-middle mb-0">
                                              <thead>
                                                <tr>
                                                  <th>Computer ID</th>
                                                  <th>Balance</th>
                                                  <th>Winning Bal.</th>
                                                  <th>Status</th>
                                                  <th>Bets</th>
                                                  <th>W / L</th>
                                                  <th>Pending</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {shopComputersList.map((comp) => {
                                                  const cg = comp.game_summary || {};
                                                  return (
                                                    <tr key={comp.id}>
                                                      <td>
                                                        <div className="hierarchy-player-identity">
                                                          <div className="hierarchy-avatar-container shop">
                                                            <i className="bi bi-pc-display text-warning hierarchy-avatar-icon" />
                                                          </div>
                                                          <div>
                                                            <strong className="text-warning">
                                                              💻 {comp.computer_code}
                                                            </strong>
                                                            <small>ID #{comp.id}</small>
                                                          </div>
                                                        </div>
                                                      </td>
                                                      <td className="fw-bold text-success">
                                                        🪙 {money(comp.current_balance)}
                                                      </td>
                                                      <td className="fw-bold text-info">
                                                        🪙 {money(comp.winning_balance)}
                                                      </td>
                                                      <td>
                                                        <span
                                                          className={`status-pill ${comp.status === "ACTIVE"
                                                              ? "active"
                                                              : "blocked"
                                                            }`}
                                                        >
                                                          {comp.status}
                                                        </span>
                                                      </td>
                                                      <td>{cg.total_bets || 0}</td>
                                                      <td>
                                                        {cg.total_wins || 0} / {cg.total_losses || 0}
                                                      </td>
                                                      <td>{cg.pending_claims || 0}</td>
                                                    </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                          </div>
                                        ) : shopPlayersList.length > 0 ? (
                                          <div className="table-responsive">
                                            <table className="table hierarchy-player-table align-middle mb-0">
                                              <thead>
                                                <tr>
                                                  <th>Player</th>
                                                  <th>Mobile</th>
                                                  <th>Wallet</th>
                                                  <th>Status</th>
                                                  <th>Bets</th>
                                                  <th>W / L</th>
                                                  <th>Pending</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {shopPlayersList.map(
                                                  (player) => {
                                                    const pg =
                                                      player.game_summary || {};
                                                    return (
                                                      <tr key={player.id}>
                                                        <td>
                                                          <div className="hierarchy-player-identity">
                                                            <UserAvatar
                                                              url={
                                                                player.avatar_url
                                                              }
                                                              name={
                                                                player.full_name ||
                                                                player.username
                                                              }
                                                              role="player"
                                                            />
                                                            <div>
                                                              <strong>
                                                                {player.full_name ||
                                                                  player.username}
                                                              </strong>
                                                              <small>
                                                                @{player.username} · #
                                                                {player.id}
                                                              </small>
                                                            </div>
                                                          </div>
                                                        </td>
                                                        <td>
                                                          {player.mobile || "-"}
                                                        </td>
                                                        <td className="fw-bold text-success">
                                                          🪙 {money(player.wallet)}
                                                        </td>
                                                        <td>
                                                          <span
                                                            className={`status-pill ${player.status ===
                                                                "ACTIVE"
                                                                ? "active"
                                                                : "blocked"
                                                              }`}
                                                          >
                                                            {player.status}
                                                          </span>
                                                        </td>
                                                        <td>
                                                          {pg.total_bets || 0}
                                                        </td>
                                                        <td>
                                                          {pg.total_wins || 0} /{" "}
                                                          {pg.total_losses || 0}
                                                        </td>
                                                        <td>
                                                          {pg.pending_claims || 0}
                                                        </td>
                                                      </tr>
                                                    );
                                                  }
                                                )}
                                              </tbody>
                                            </table>
                                          </div>
                                        ) : (
                                          <div className="hierarchy-no-player">
                                            No computers registered in this shop.
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminHierarchy;